import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  keyframes,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Info as InfoIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Calculate as CalculateIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BusinessIcon from "@mui/icons-material/Business";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useGetMarketplaceActualTaxProfile } from "../../../client/hooks/marketplace/marketplace.queries";

// Estilos comuns modernizados
const commonStyles = {
  card: {
    p: 3,
    bgcolor: "white",
    borderRadius: 2,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    },
  },
  contentBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
  },
  valueBox: {
    flex: "1 1 200px",
    bgcolor: "#fafafa",
    p: 2,
    borderRadius: 2,
    transition: "all 0.2s ease",
    border: "1px solid #f0f0f0",
    "&:hover": {
      backgroundColor: "#f8f8f8",
      transform: "translateY(-2px)",
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    mb: 2,
    "& .icon": {
      color: "primary.main",
      fontSize: 24,
    },
    "& .title": {
      fontWeight: 600,
      color: "text.primary",
    },
  },
};

const pulse = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const TaxValueDisplay = ({ label, value, description, icon }) => (
  <Box sx={commonStyles.valueBox}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(255, 130, 102, 0.1)",
          borderRadius: "50%",
          p: 0.8,
          width: 32,
          height: 32,
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ flex: 1, fontWeight: 500 }}
      >
        {label}
      </Typography>
      {description && (
        <Tooltip title={description} arrow placement="top">
          <IconButton size="small" sx={{ p: 0.5 }}>
            <InfoIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 600,
        color: "primary.main",
        fontSize: "1rem",
      }}
    >
      {typeof value === "boolean" ? (value ? "Sim" : "Não") : value}
    </Typography>
  </Box>
);

const TaxSection = ({ title, icon, children, color = "primary.main" }) => (
  <Box
    sx={{
      ...commonStyles.card,
      borderLeft: `4px solid ${color}`,
      borderTop: "none",
      mb: 2.5,
      boxShadow: "0 3px 10px rgba(0,0,0,0.04)",
    }}
  >
    <Box sx={commonStyles.sectionHeader}>
      {React.cloneElement(icon, { className: "icon", style: { color } })}
      <Typography
        variant="subtitle1"
        className="title"
        sx={{ fontSize: "1rem" }}
      >
        {title}
      </Typography>
    </Box>
    <Divider sx={{ mt: 1, mb: 2, opacity: 0.6 }} />
    {children}
  </Box>
);

const TaxRegimeInfo = ({ open, onConfigureClick }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formattedSpecialConfig, setFormattedSpecialConfig] = useState(null);

  // Hook para buscar dados do backend
  const { data: taxData, isLoading, error } = useGetMarketplaceActualTaxProfile();

  // Estados para dados atuais
  const [selectedRegime, setSelectedRegime] = useState("");
  const [regimeConfig, setRegimeConfig] = useState(null);
  const [specialRegimeEnabled, setSpecialRegimeEnabled] = useState(false);

  // Efeito para carregar dados do backend quando chegarem
  useEffect(() => {
    if (taxData?.tax) {
      const { tax } = taxData;
      
      setSelectedRegime(tax.regime);
      setSpecialRegimeEnabled(tax.regimeEspecialEnabled);
      
      // Configura os dados baseados no regime específico
      let backendConfig = {};
      
      if (tax.regime === "SIMPLES_NACIONAL") {
        backendConfig = {
          icmsAliquota: tax.icmsAliquota,
          icmsFreteChecked: tax.icmsAliquotaSellerFreightEnabled,
          pisCofinsSomarFreteComissao: tax.pisCofinsSellerFreightEnabled,
          pisCofinsPercentage: tax.pisCofinsRegimeSimples || "4",
          icmsPercentage: tax.icmsRegimeSimples || "3",
          pisCofins: tax.pisCofinsRegimeSimples || "4",
          diffal: "Será uma %(porcentagem) sobre o valor da venda, conforme o estado de destino."
        };
      } else if (tax.regime === "LUCRO_PRESUMIDO") {
        // LUCRO_PRESUMIDO - valor específico
        backendConfig = {
          icmsAliquota: tax.icmsAliquota,
          icmsFreteChecked: tax.icmsAliquotaSellerFreightEnabled,
          pisCofinsSomarFreteComissao: tax.pisCofinsSellerFreightEnabled,
          pisCofinsPercentage: tax.pisCofins || "4",
          icmsPercentage: tax.icmsRegimeSimples || "12",
          pisCofins: tax.pisCofins || "4",
          diffal: "Será uma %(porcentagem) sobre o valor da venda, conforme o estado de destino."
        };
      } else {
        // LUCRO_REAL
        backendConfig = {
          icmsAliquota: tax.icmsAliquota,
          icmsFreteChecked: tax.icmsAliquotaSellerFreightEnabled,
          pisCofinsSomarFreteComissao: tax.pisCofinsSellerFreightEnabled,
          pisCofinsPercentage: tax.pisCofins || "3.65",
          icmsPercentage: tax.icmsRegimeSimples || "12",
          pisCofins: tax.pisCofins || "3.65",
          diffal: "Será uma %(porcentagem) sobre o valor da venda, conforme o estado de destino."
        };
      }
      
      setRegimeConfig(backendConfig);
      
      // Configura regime especial se estiver habilitado
      if (tax.regimeEspecialEnabled && tax.regimeEspecial) {
        setFormattedSpecialConfig({
          icmsNacional: tax.regimeEspecial.icms_nacional,
          icmsImportado: tax.regimeEspecial.icms_importado,
          vendasDentroEstado: "Para vendas dentro do mesmo estado, será aplicado o sistema de débito/crédito de ICMS",
        });
      } else {
        setFormattedSpecialConfig(null);
      }
    } else {
      // Fallback para localStorage se não houver dados do backend
      const localRegime = localStorage.getItem("selected_tax_regime") || "";
      const localConfig = localRegime
        ? JSON.parse(
            localStorage.getItem(
              `tax_regime_config_${localRegime.toLowerCase()}`
            ) || "{}"
          )
        : {};
      const localSpecialEnabled = localStorage.getItem("special_regime_enabled") === "true";
      
      // Configura dados padronizados do localStorage baseado no regime
      let fallbackConfig = {};
      
      if (localRegime === "SIMPLES_NACIONAL") {
        fallbackConfig = {
          icmsAliquota: localConfig.icmsAliquota || "12",
          icmsFreteChecked: localConfig.icmsFreteChecked || false,
          pisCofinsSomarFreteComissao: localConfig.pisCofinsSomarFreteComissao || false,
          pisCofinsPercentage: localConfig.pisCofinsPercentage || "4",
          icmsPercentage: localConfig.icmsPercentage || "3",
          pisCofins: localConfig.pisCofins || "4",
          diffal: "Será uma %(porcentagem) sobre o valor da venda, conforme o estado de destino."
        };
      } else if (localRegime === "LUCRO_PRESUMIDO") {
        fallbackConfig = {
          icmsAliquota: localConfig.icmsAliquota || "12",
          icmsFreteChecked: localConfig.icmsFreteChecked || false,
          pisCofinsSomarFreteComissao: localConfig.pisCofinsSomarFreteComissao || false,
          pisCofinsPercentage: localConfig.pisCofinsPercentage || "4",
          icmsPercentage: localConfig.icmsPercentage || "12",
          pisCofins: localConfig.pisCofins || "4",
          diffal: "Será uma %(porcentagem) sobre o valor da venda, conforme o estado de destino."
        };
      } else {
        // LUCRO_REAL
        fallbackConfig = {
          icmsAliquota: localConfig.icmsAliquota || "12",
          icmsFreteChecked: localConfig.icmsFreteChecked || false,
          pisCofinsSomarFreteComissao: localConfig.pisCofinsSomarFreteComissao || false,
          pisCofinsPercentage: localConfig.pisCofinsPercentage || "3.65",
          icmsPercentage: localConfig.icmsPercentage || "12",
          pisCofins: localConfig.pisCofins || "3.65",
          diffal: "Será uma %(porcentagem) sobre o valor da venda, conforme o estado de destino."
        };
      }
      
      setSelectedRegime(localRegime);
      setRegimeConfig(fallbackConfig);
      setSpecialRegimeEnabled(localSpecialEnabled);
      
      // Carrega configurações especiais do localStorage
      if (localSpecialEnabled && localRegime) {
        try {
          const specialConfigStr =
            localStorage.getItem(`special_regime_config_${localRegime}`) || "{}";
          const parsedConfig = JSON.parse(specialConfigStr);

          setFormattedSpecialConfig({
            icmsNacional: parsedConfig?.icms_nacional || null,
            icmsImportado: parsedConfig?.icms_importado || null,
            vendasDentroEstado: parsedConfig?.vendasDentroEstado || null,
          });
        } catch (error) {
          console.error("Erro ao carregar configurações especiais:", error);
          setFormattedSpecialConfig(null);
        }
      } else {
        setFormattedSpecialConfig(null);
      }
    }
  }, [taxData]);

  const getRegimeName = (regime) =>
    ({
      LUCRO_REAL: "Lucro Real",
      SIMPLES_NACIONAL: "Simples Nacional",
      LUCRO_PRESUMIDO: "Lucro Presumido",
    })[regime] || regime;

  const handleConfigureClick = () => {
    onConfigureClick?.();
    setDialogOpen(false);
  };

  const renderLucroRealContent = () => (
    <>
      <TaxSection title="ICMS" icon={<CalculateIcon />}>
        <Box sx={commonStyles.contentBox}>
          <TaxValueDisplay
            label="ICMS Alíquota"
            value={`${regimeConfig.icmsAliquota || '0'}%`}
            description="Alíquota do ICMS configurada"
            icon={
              <CalculateIcon sx={{ color: "text.secondary", fontSize: 20 }} />
            }
          />
          <TaxValueDisplay
            label="Incluir Frete no ICMS"
            value={regimeConfig.icmsFreteChecked}
            description="Indica se o frete deve ser incluído no cálculo do ICMS"
            icon={
              <CalculateIcon sx={{ color: "text.secondary", fontSize: 20 }} />
            }
          />
        </Box>
      </TaxSection>

      <TaxSection title="PIS/COFINS" icon={<AssignmentIcon />} color="#00796b">
        <Box sx={commonStyles.contentBox}>
          <TaxValueDisplay
            label="Incluir Frete e Comissão"
            value={regimeConfig.pisCofinsSomarFreteComissao}
            description="Indica se frete e comissão devem ser incluídos no cálculo"
            icon={
              <CalculateIcon sx={{ color: "text.secondary", fontSize: 20 }} />
            }
          />
        </Box>
      </TaxSection>

      <TaxSection title="DIFAL" icon={<CalculateIcon />} color="#0277bd">
        <Typography variant="body2" color="text.secondary">
          {regimeConfig.diffal}
        </Typography>
      </TaxSection>
    </>
  );

  const renderSimplesNacionalContent = () => (
    <>
      <TaxSection title="Alíquotas" icon={<CalculateIcon />}>
        <Box sx={commonStyles.contentBox}>
          <TaxValueDisplay
            label="ICMS"
            value={`${regimeConfig.icmsPercentage}%`}
            description="Alíquota de ICMS para Simples Nacional"
            icon={
              <AssignmentIcon sx={{ color: "text.secondary", fontSize: 20 }} />
            }
          />
          <TaxValueDisplay
            label="PIS/COFINS"
            value={`${regimeConfig.pisCofinsPercentage}%`}
            description="Alíquota unificada de PIS/COFINS"
            icon={
              <AssignmentIcon sx={{ color: "text.secondary", fontSize: 20 }} />
            }
          />
        </Box>
      </TaxSection>
    </>
  );

  const renderLucroPresumidoContent = () => {
    if (!regimeConfig) {
      return (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Carregando configurações...
          </Typography>
        </Box>
      );
    }
    
    return (
      <>
        <TaxSection title="ICMS" icon={<CalculateIcon />}>
          <Box sx={commonStyles.contentBox}>
            <TaxValueDisplay
              label="ICMS Alíquota"
              value={`${regimeConfig.icmsAliquota || '12'}%`}
              description="Alíquota do ICMS configurada"
              icon={
                <CalculateIcon sx={{ color: "text.secondary", fontSize: 20 }} />
              }
            />
            <TaxValueDisplay
              label="Incluir Frete no ICMS"
              value={regimeConfig.icmsFreteChecked ? "Sim" : "Não"}
              description="Indica se o frete deve ser incluído no cálculo do ICMS"
              icon={
                <CalculateIcon sx={{ color: "text.secondary", fontSize: 20 }} />
              }
            />
          </Box>
        </TaxSection>

        <TaxSection title="PIS/COFINS" icon={<AssignmentIcon />} color="#00796b">
          <Box sx={commonStyles.contentBox}>
            <TaxValueDisplay
              label="PIS/COFINS"
              value={`${regimeConfig.pisCofinsPercentage || regimeConfig.pisCofins || '4'}%`}
              description="Porcentagem de PIS/COFINS aplicada sobre as vendas"
              icon={
                <CalculateIcon sx={{ color: "text.secondary", fontSize: 20 }} />
              }
            />
          </Box>
        </TaxSection>

        <TaxSection title="DIFAL" icon={<CalculateIcon />} color="#0277bd">
          <Typography variant="body2" color="text.secondary">
            {regimeConfig.diffal || "Será uma %(porcentagem) sobre o valor da venda, conforme o estado de destino."}
          </Typography>
        </TaxSection>
      </>
    );
  };
  const renderSpecialRegimeSection = () => (
    <TaxSection
      title="Configurações Especiais"
      icon={<SettingsIcon />}
      color="#9c27b0"
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Chip
            icon={specialRegimeEnabled ? <CheckCircleIcon /> : <CancelIcon />}
            label={specialRegimeEnabled ? "Habilitado" : "Desabilitado"}
            color={specialRegimeEnabled ? "secondary" : "default"}
            variant={specialRegimeEnabled ? "filled" : "outlined"}
            sx={{ fontWeight: 500, borderRadius: "50px", px: 1 }}
          />
        </Box>
        {specialRegimeEnabled && formattedSpecialConfig && (
          <>
            <Box
              sx={{
                mb: 2,
                backgroundColor: "rgba(156, 39, 176, 0.05)",
                p: 2,
                borderRadius: 2,
                border: "1px solid rgba(156, 39, 176, 0.1)",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 1, color: "#9c27b0" }}
              >
                Vendas Dentro do Estado
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.6 }}
              >
                Para vendas dentro do mesmo estado, será aplicado o sistema de
                débito/crédito de ICMS
              </Typography>
            </Box>
            <Box sx={commonStyles.contentBox}>
              <TaxValueDisplay
                label="ICMS Nacional"
                value={
                  formattedSpecialConfig.icmsNacional !== null
                    ? `${formattedSpecialConfig.icmsNacional}%`
                    : "Não configurado"
                }
                description="ICMS especial para produtos nacionais"
                icon={
                  <AssignmentIcon
                    sx={{ color: "text.secondary", fontSize: 20 }}
                  />
                }
              />
              <TaxValueDisplay
                label="ICMS Importado"
                value={
                  formattedSpecialConfig.icmsImportado !== null
                    ? `${formattedSpecialConfig.icmsImportado}%`
                    : "Não configurado"
                }
                description="ICMS especial para produtos importados"
                icon={
                  <AssignmentIcon
                    sx={{ color: "text.secondary", fontSize: 20 }}
                  />
                }
              />
            </Box>
          </>
        )}
      </Box>
    </TaxSection>
  );
  // O conteúdo é renderizado diretamente no Dialog agora

  return (
    <>
      <div style={{ width: open ? "16rem" : "" }}>
        <ListItemButton
          onClick={() => setDialogOpen(true)}
          sx={{
            borderRadius: 1,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(46, 125, 50, 0.08)",
            },
          }}
        >
          <ListItemIcon
            sx={{
              display: open ? "" : "flex",
              justifyContent: open ? "" : "center",
              minWidth: 43,
            }}
          >
            {selectedRegime ? (
              selectedRegime === "LUCRO_REAL" ? (
                <AccountBalanceIcon sx={{ color: "primary.main" }} />
              ) : selectedRegime === "SIMPLES_NACIONAL" ? (
                <BusinessIcon sx={{ color: "primary.main" }} />
              ) : (
                <TrendingUpIcon sx={{ color: "primary.main" }} />
              )
            ) : (
              <WarningIcon
                sx={{
                  color: "#ed6c02",
                  animation: `${pulse} 2s infinite ease-in-out`,
                }}
              />
            )}
          </ListItemIcon>
          {open && (
            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              sx={{ mr: 1 }}
            />
          )}
          {open && (
            <ListItemText
              primary={
                selectedRegime ? (
                  getRegimeName(selectedRegime)
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      sx={{
                        color: "#ed6c02",
                        fontWeight: 500,
                        lineHeight: 1.2,
                      }}
                    >
                      Regime Tributário
                    </Typography>
                    <Typography
                      sx={{
                        color: "#ed6c02",
                        fontWeight: 500,
                        lineHeight: 1.2,
                      }}
                    >
                      não configurado
                    </Typography>
                  </Box>
                )
              }
              secondary={
                selectedRegime
                  ? "Clique para ver detalhes"
                  : "Clique para configurar"
              }
              sx={{
                "& .MuiListItemText-primary": {
                  color: selectedRegime ? "text.primary" : "#ed6c02",
                  fontWeight: 500,
                },
                "& .MuiListItemText-secondary": {
                  fontSize: "0.75rem",
                },
              }}
            />
          )}
        </ListItemButton>
      </div>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 4,
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "#fafafa",
          },
        }}
      >
        <Box
          sx={{
            p: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {selectedRegime &&
              (selectedRegime === "LUCRO_REAL" ? (
                <AccountBalanceIcon sx={{ color: "#1976d2", fontSize: 28 }} />
              ) : selectedRegime === "SIMPLES_NACIONAL" ? (
                <BusinessIcon sx={{ color: "#2e7d32", fontSize: 28 }} />
              ) : (
                <TrendingUpIcon sx={{ color: "#ed6c02", fontSize: 28 }} />
              ))}
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, fontSize: "1.25rem" }}
            >
              {selectedRegime
                ? `Regime: ${getRegimeName(selectedRegime)}`
                : "Configuração do Regime Tributário"}
            </Typography>
          </Box>
          <IconButton onClick={() => setDialogOpen(false)} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            p: 3,
            bgcolor: "#fafafa",
            maxHeight: "70vh",
            overflowY: "auto",
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                p: 4,
              }}
            >
              <CircularProgress size={40} />
              <Typography variant="body1" color="text.secondary">
                Carregando configurações tributárias...
              </Typography>
            </Box>
          ) : error ? (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={() => window.location.reload()}
                >
                  Tentar Novamente
                </Button>
              }
            >
              Erro ao carregar dados: {error.message}
            </Alert>
          ) : selectedRegime && regimeConfig ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {(() => {
                switch (selectedRegime) {
                  case "LUCRO_REAL":
                    return renderLucroRealContent();
                  case "SIMPLES_NACIONAL":
                    return renderSimplesNacionalContent();
                  case "LUCRO_PRESUMIDO":
                    return renderLucroPresumidoContent();
                  default:
                    return null;
                }
              })()}
              {selectedRegime !== "SIMPLES_NACIONAL" &&
                renderSpecialRegimeSection()}
            </Box>
          ) : (
            <Box
              sx={{
                p: 3,
                textAlign: "center",
                bgcolor: "white",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "warning.light",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <WarningIcon color="warning" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                É necessário configurar um regime tributário para prosseguir com
                as operações. Por favor, acesse as configurações de impostos.
              </Typography>
              <Button
                variant="contained"
                color="warning"
                onClick={handleConfigureClick}
                startIcon={<SettingsIcon />}
                sx={{ px: 3 }}
              >
                Configurar Agora
              </Button>
            </Box>
          )}
        </DialogContent>

        {selectedRegime && (
          <DialogActions
            sx={{
              px: 3,
              py: 2,
              bgcolor: "background.paper",
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button
              onClick={() => setDialogOpen(false)}
              variant="outlined"
              color="inherit"
              sx={{
                borderRadius: "50px",
                px: 3,
                fontWeight: 500,
              }}
            >
              Fechar
            </Button>
            <Button
              onClick={handleConfigureClick}
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              sx={{
                borderRadius: "50px",
                px: 3,
                fontWeight: 500,
              }}
            >
              Editar Configurações
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default TaxRegimeInfo;
